import { useState } from 'react';
import { Target, CheckCircle2, ArrowRight, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { parseRubricData, ParsedDimension, ChatcraftScores, DIMENSIONS } from '@/lib/rubricParser';
import { aggregateParsedDimensionsToCategories, CategoryScore } from '@/lib/rubricCategories';

// Dimension scores can be either a number or an object with score/rationale
type DimensionScoreValue = number | { score: number; rationale?: string };

interface ScoreBreakdownProps {
  overallScore: number;
  evaluationMarkdown?: string;
  dimensionScores?: Record<string, DimensionScoreValue>;
  topUpgrades?: string[];
  isChat?: boolean;
  chatcraftScoresJson?: Record<string, number>; // DB-stored chatcraft scores (snake_case keys)
  collapsible?: boolean; // When true, starts collapsed (for mobile)
}

// Static helper text for Chatcraft criteria
const CHATCRAFT_HELPERS = {
  convergence: {
    label: 'Convergence efficiency',
    max: 12,
    whatItMeans: 'How quickly you reach a quality result with minimal back-and-forth.',
    improveNextTime: 'Front-load context and constraints to reduce revision cycles.',
  },
  constraintMastery: {
    label: 'Constraint mastery',
    max: 10,
    whatItMeans: 'Your ability to add and refine constraints throughout the conversation.',
    improveNextTime: 'Add explicit boundaries early and tighten scope as you iterate.',
  },
  sessionHygiene: {
    label: 'Session hygiene + drift',
    max: 8,
    whatItMeans: 'Managing context well and recognizing when to start fresh.',
    improveNextTime: 'Start a new chat when switching topics to avoid context confusion.',
  },
};

// Beginner category bar with Doing well / Improve feedback
function CategoryRow({ category }: { category: CategoryScore }) {
  const percentage = category.percentOfMax * 100;
  const isExcellent = percentage >= 90;
  
  return (
    <div className="py-2.5 border-b border-border/40 last:border-0">
      {/* Row header: category name + score */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-base font-medium text-foreground leading-tight">
          {category.label}
        </span>
        <span className="text-base font-mono text-muted-foreground shrink-0">
          {Math.round(category.score)} / {category.max}
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="h-1 bg-muted/60 rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full rounded-full transition-all ${
            percentage >= 60 ? 'bg-success' : 'bg-primary/80'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {/* Two bullet lines */}
      <div className="space-y-1">
        <div className="flex items-start gap-1.5">
          <CheckCircle2 className="h-3 w-3 text-success shrink-0 mt-0.5" />
          <span className="text-base text-muted-foreground leading-snug">
            {category.doingWell}
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <ArrowRight className={`h-3 w-3 shrink-0 mt-0.5 ${isExcellent ? 'text-success' : 'text-warning'}`} />
          <span className={`text-base leading-snug ${isExcellent ? 'text-success' : 'text-muted-foreground'}`}>
            {category.improve}
          </span>
        </div>
      </div>
    </div>
  );
}

function DimensionRow({ dimension }: { dimension: ParsedDimension }) {
  const percentage = (dimension.score / dimension.max) * 100;
  const isExcellent = percentage >= 90;
  
  return (
    <div className="py-2.5 border-b border-border/40 last:border-0">
      {/* Row header: dimension name + score */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-base font-medium text-foreground leading-tight">
          {dimension.label}
        </span>
        <span className="text-base font-mono text-muted-foreground shrink-0">
          {dimension.score} / {dimension.max}
        </span>
      </div>
      
      {/* Mini bar - full width, thin */}
      <div className="h-1 bg-muted/60 rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full rounded-full transition-all ${
            percentage >= 60 ? 'bg-success' : 'bg-primary/80'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {/* Two bullet lines */}
      <div className="space-y-1">
        <div className="flex items-start gap-1.5">
          <CheckCircle2 className="h-3 w-3 text-success shrink-0 mt-0.5" />
          <span className="text-base text-muted-foreground leading-snug">
            {dimension.whatWorked}
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <ArrowRight className={`h-3 w-3 shrink-0 mt-0.5 ${isExcellent ? 'text-success' : 'text-warning'}`} />
          <span className={`text-base leading-snug ${isExcellent ? 'text-success' : 'text-muted-foreground'}`}>
            {dimension.improvement}
          </span>
        </div>
      </div>
    </div>
  );
}

function ChatcraftRow({ 
  criterionKey, 
  score 
}: { 
  criterionKey: keyof typeof CHATCRAFT_HELPERS; 
  score: number;
}) {
  const helper = CHATCRAFT_HELPERS[criterionKey];
  const percentage = (score / helper.max) * 100;
  const isExcellent = percentage >= 90;
  
  return (
    <div className="py-2.5 border-b border-border/40 last:border-0">
      {/* Row header: criterion name + score */}
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-base font-medium text-foreground leading-tight">
          {helper.label}
        </span>
        <span className="text-base font-mono text-muted-foreground shrink-0">
          {score} / {helper.max}
        </span>
      </div>
      
      {/* Full-width bar matching dimension rows */}
      <div className="h-1 bg-muted/60 rounded-full overflow-hidden mb-2">
        <div 
          className="h-full bg-secondary/80 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {/* Two helper lines */}
      <div className="space-y-1">
        <div className="flex items-start gap-1.5">
          <Info className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
          <span className="text-base text-muted-foreground leading-snug">
            {helper.whatItMeans}
          </span>
        </div>
        <div className="flex items-start gap-1.5">
          <ArrowRight className={`h-3 w-3 shrink-0 mt-0.5 ${isExcellent ? 'text-success' : 'text-warning'}`} />
          <span className={`text-base leading-snug ${isExcellent ? 'text-success' : 'text-muted-foreground'}`}>
            {isExcellent ? 'Keep doing this.' : helper.improveNextTime}
          </span>
        </div>
      </div>
    </div>
  );
}

function ChatcraftSection({ chatcraft }: { chatcraft: ChatcraftScores }) {
  return (
    <div className="pb-4 mb-2 border-b border-border/50">
      <h4 className="text-sm font-semibold mb-3 text-foreground flex items-center justify-between">
        <span>Chat-Specific Criteria</span>
        <span className="text-xs font-mono text-muted-foreground">
          {chatcraft.total}/30
        </span>
      </h4>
      <div className="divide-y divide-border/30">
        <ChatcraftRow criterionKey="convergence" score={chatcraft.convergence} />
        <ChatcraftRow criterionKey="constraintMastery" score={chatcraft.constraintMastery} />
        <ChatcraftRow criterionKey="sessionHygiene" score={chatcraft.sessionHygiene} />
      </div>
    </div>
  );
}

export function ScoreBreakdown({ 
  overallScore, 
  evaluationMarkdown = '', 
  dimensionScores,
  topUpgrades,
  isChat = false,
  chatcraftScoresJson,
  collapsible = false
}: ScoreBreakdownProps) {
  const [isExpanded, setIsExpanded] = useState(!collapsible);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Parse the rubric data - pass chatcraft scores for DB-first lookup
  const parsed = parseRubricData(
    evaluationMarkdown, 
    overallScore,
    dimensionScores,
    chatcraftScoresJson
  );
  
  // Aggregate dimensions into beginner categories
  const categories = aggregateParsedDimensionsToCategories(parsed.dimensions);

  return (
    <Card className="border-border/50 bg-card fade-in-up opacity-0 stagger-3">
      <CardHeader className="pb-3">
        <button 
          className="w-full"
          onClick={() => collapsible && setIsExpanded(!isExpanded)}
          disabled={!collapsible}
        >
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-base">
              <Target className="h-5 w-5 text-primary" />
              Score Breakdown
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-foreground">{overallScore}</span>
              <span className="text-sm text-muted-foreground">/100</span>
              {collapsible && (
                isExpanded ? (
                  <ChevronUp className="h-4 w-4 text-muted-foreground ml-1" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground ml-1" />
                )
              )}
            </div>
          </CardTitle>
        </button>
        {/* Overall progress bar - always visible */}
        <div className="h-2 bg-muted/60 rounded-full overflow-hidden mt-2">
          <div 
            className={`h-full rounded-full transition-all ${
              overallScore >= 80 ? 'bg-success' : 'bg-primary/80'
            }`}
            style={{ width: `${overallScore}%` }}
          />
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-0">
          {/* Chatcraft section FIRST for chat submissions */}
          {isChat && parsed.chatcraft && (
            <ChatcraftSection chatcraft={parsed.chatcraft} />
          )}
          
          {!showAdvanced ? (
            /* Beginner View: 4 category bars */
            <div className="divide-y divide-border/30">
              {categories.map((cat) => (
                <CategoryRow key={cat.key} category={cat} />
              ))}
            </div>
          ) : (
            /* Advanced View: All 10 dimensions */
            <div className="divide-y divide-border/30">
              {parsed.dimensions.map((dim) => (
                <DimensionRow key={dim.key} dimension={dim} />
              ))}
            </div>
          )}

          {/* Toggle button */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-4 text-muted-foreground hover:text-foreground"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Hide Advanced Metrics
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Advanced Metrics
              </>
            )}
          </Button>
        </CardContent>
      )}
    </Card>
  );
}
